import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from "../ui/card";
import { Alert, AlertDescription } from "../ui/alert";

const ALL_BASE_URLS = {
  aelf: 'https://www.aelf.org/bible/book/chapter',
  lsg: 'https://saintebible.com/lsg/book/chapter.htm',
  mar: 'https://saintebible.com/mar/book/chapter.htm',
  dar: 'https://saintebible.com/dar/book/chapter.htm',
  drb: 'https://biblehub.com/drb/book/chapter.htm',
  kjv: 'https://biblehub.com/kjv/book/chapter.htm',
  niv: 'https://biblehub.com/niv/book/chapter.htm',
  esv: 'https://biblehub.com/esv/book/chapter.htm'
};

const SAINTEBIBLE_BOOK_MAPPING = {
  'gn': 'genesis',
  'ex': 'exodus', 
  'lv': 'leviticus',
  'nb': 'numbers',
  'dt': 'deuteronomy',
  'js': 'joshua',
  'jg': 'judges',
  'rt': 'ruth',
  '1s': '1_samuel',
  '2s': '2_samuel',
  '1r': '1_kings',
  '2r': '2_kings',
  '1ch': '1_chronicles',
  '2ch': '2_chronicles',
  'esd': 'ezra',
  'ne': 'nehemiah',
  'tb': 'tobit',
  'jdt': 'judith',
  'est': 'esther',
  '1m': '1_maccabees',
  '2m': '2_maccabees',
  'jb': 'job',
  'ps': 'psalms',
  'pr': 'proverbs',
  'qo': 'ecclesiastes',
  'ct': 'songs',
  'sg': 'wisdom',
  'si': 'sirach',
  'is': 'isaiah',
  'jr': 'jeremiah',
  'lm': 'lamentations',
  'ba': 'baruch',
  'ez': 'ezekiel',
  'dn': 'daniel',
  'os': 'hosea',
  'jl': 'joel',
  'am': 'amos',
  'ab': 'obadiah',
  'jon': 'jonah',
  'mi': 'micah',
  'na': 'nahum',
  'ha': 'habakkuk',
  'so': 'zephaniah',
  'ag': 'haggai',
  'za': 'zechariah',
  'ml': 'malachi',
  'mt': 'matthew',
  'mc': 'mark',
  'lc': 'luke',
  'jn': 'john',
  'ac': 'acts',
  'rm': 'romans',
  '1co': '1_corinthians',
  '2co': '2_corinthians',
  'ga': 'galatians',
  'ep': 'ephesians',
  'ph': 'philippians',
  'col': 'colossians',
  '1th': '1_thessalonians',
  '2th': '2_thessalonians',
  '1tm': '1_timothy',
  '2tm': '2_timothy',
  'tt': 'titus',
  'phm': 'philemon',
  'he': 'hebrews',
  'jc': 'james',
  '1p': '1_peter',
  '2p': '2_peter',
  '1jn': '1_john',
  '2jn': '2_john',
  '3jn': '3_john',
  'ju': 'jude',
  'ap': 'revelation'
};

// Helper function to parse query parameters
const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

export const RedirectPage = () => {
  const { book, chapter, verses } = useParams<{
    book: string;
    chapter: string;
    verses?: string;
  }>();

  const query = useQuery();
  const langParam = query.get('lang');
  const translationParam = query.get('translation');
  
  const { t, i18n } = useTranslation();
  const [validationError, setValidationError] = useState<string | null>(null);
  const [selectedTranslation, setSelectedTranslation] = useState<string>('');
  const [isParameterDriven, setIsParameterDriven] = useState<boolean>(false);

  useEffect(() => {
    // If translation parameter is provided, it takes highest priority
    if (translationParam && Object.keys(ALL_BASE_URLS).includes(translationParam)) {
      setSelectedTranslation(translationParam);
      setIsParameterDriven(true);
      
      // Always redirect immediately if translation and book/chapter are present
      if (book && chapter) {
        handleRedirect(translationParam, book, chapter);
        return;
      }
    } else {
      // Only change language if no translation parameter is provided
      // If language is specified in URL, change it
      if (langParam && i18n.languages.includes(langParam)) {
        i18n.changeLanguage(langParam);
      }

      // If no translation parameter, load from localStorage
      const saved = localStorage.getItem(`selectedTranslation_${i18n.language}`);
      if (saved) {
        setSelectedTranslation(saved);
        setIsParameterDriven(true); // Also consider localStorage as parameter-driven
        
        // Always redirect immediately if translation and book/chapter are present
        if (book && chapter) {
          handleRedirect(saved, book, chapter);
          return;
        }
      }
    }
  }, [book, chapter, i18n, i18n.language, langParam, translationParam]);

  const getSainteBibleBook = (acronym: string): string => {
    return SAINTEBIBLE_BOOK_MAPPING[acronym.toLowerCase() as keyof typeof SAINTEBIBLE_BOOK_MAPPING] || acronym;
  };

  // Add a non-blocking preloadCache function
  const preloadCache = (translation: string, book: string, chapter: string) => {
    // Construct the passage string
    const passage = `${book} ${chapter}`;
    
    // Call the pingCache endpoint - prefer deployed endpoint for reliability
    const pingCacheUrl = 'https://europe-west1-bible4md.cloudfunctions.net/pingCache';
    
    // Fire and forget - send the request but don't wait for it to complete
    fetch(pingCacheUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Origin': window.location.origin
      },
      body: JSON.stringify({
        data: {
          bible: translation,
          passage: passage
        }
      })
    }).then(response => {
      console.log(`Cache ping request sent for ${translation} ${passage}`);
    }).catch(error => {
      console.error('Error pinging cache:', error);
    });
    
    // No return value since we're not waiting
  };

  const handleRedirect = (translation: string, book: string, chapter: string) => {
    if (!translation || !book || !chapter) return;

    const baseUrl = ALL_BASE_URLS[translation as keyof typeof ALL_BASE_URLS];
    if (!baseUrl) {
      setValidationError(t('errors.invalidTranslation', { translation }));
      return;
    }

    // Start preloading the cache in the background - no await
    preloadCache(translation, book, chapter);
    
    // Immediately redirect without waiting
    const bookName = getSainteBibleBook(book);
    let url = baseUrl.includes('saintebible.com') || baseUrl.includes('biblehub.com')
      ? baseUrl.replace('book', bookName).replace('chapter', chapter)
      : baseUrl.replace('book', book).replace('chapter', chapter);

    // Append the translation as a query parameter if not already present
    const hasQuery = url.includes('?');
    url += hasQuery ? `&t=${translation}` : `?t=${translation}`;

    window.location.href = url;
  };

  const handleTranslationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedTranslation(e.target.value);
    setValidationError(null);
  };

  const handleSaveAndContinue = () => {
    if (!selectedTranslation) {
      setValidationError(t('errors.selectTranslation'));
      return;
    }

    localStorage.setItem(`selectedTranslation_${i18n.language}`, selectedTranslation);
    handleRedirect(selectedTranslation, book || '', chapter || '');
  };

  const handleContinueWithoutSaving = () => {
    let translationToUse;
    
    if (selectedTranslation) {
      translationToUse = selectedTranslation;
    } else {
      // Default translations based on language
      translationToUse = i18n.language === 'fr' ? 'aelf' : 'kjv';
    }
    
    handleRedirect(translationToUse, book || '', chapter || '');
  };

  const translations = Object.entries(t('translations', { returnObjects: true }));

  // Auto redirect if parameters are available
  useEffect(() => {
    if (isParameterDriven && book && chapter) {
      handleRedirect(selectedTranslation, book, chapter);
    }
  }, [isParameterDriven, selectedTranslation, book, chapter]);

  // If validation error, show the error
  if (validationError) {
    return (
      <Alert variant="destructive" className="max-w-lg mx-auto mt-8">
        <AlertDescription>{validationError}</AlertDescription>
      </Alert>
    );
  }

  // If driven by URL parameters but missing book/chapter, show error
  if (isParameterDriven && (!book || !chapter)) {
    return (
      <Alert variant="destructive" className="max-w-lg mx-auto mt-8">
        <AlertDescription>{t('errors.missingReference')}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="min-h-screen bg-amber-100">
      <div className="bg-white border-b border-amber-200 p-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-amber-900 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-lg">B4M</span>
            </div>
            <div>
              <div className="flex items-center">
                <h3 className="font-semibold text-amber-900">{t('redirect_page.title')}</h3>
                <span className="mx-2 text-amber-400">•</span>
                <span className="text-amber-600 text-sm">{t('redirect_page.free')}</span>
              </div>
              <p className="text-amber-700 text-sm">{t('redirect_page.notes')}</p>
              <div className="text-xs text-amber-600">⭐⭐⭐⭐⭐ 4.8</div>
            </div>
          </div>
          <button className="bg-amber-900 text-white px-6 py-1.5 rounded-full text-sm font-medium">
            {t('redirect_page.install')}
          </button>
        </div>
      </div>

      <div className="flex justify-center items-center p-4" style={{ minHeight: 'calc(100vh - 84px)' }}>
        <Card className="w-full max-w-lg">
          <CardContent className="p-6">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-amber-900 mb-1">{t('redirect_page.title')}</h1>
              <p className="text-lg text-amber-700 font-medium mb-4">{t('redirect_page.subtitle')}</p>
              <div className="space-y-3">
                <p className="text-amber-900">{t('redirect_page.description')}</p>
                <a href="/app" className="inline-block text-amber-900 hover:text-amber-700 underline text-sm">
                  {t('redirect_page.learnMore')}
                </a>
                <p className="text-amber-700">{t('redirect_page.selectTranslation')}</p>
              </div>
            </div>

            <div className="space-y-4">
              {/* Only show selection if not parameter-driven */}
              {!isParameterDriven && (
                <>
                  <div className="space-y-2">
                    <label className="text-lg font-medium text-amber-900 block">
                      {t('redirect_page.translationLabel')}
                    </label>
                    <select
                      value={selectedTranslation}
                      onChange={handleTranslationChange}
                      className="w-full p-2 border rounded-md border-amber-200"
                    >
                      <option value="">{t('redirect_page.placeholder')}</option>
                      {translations.map(([code, name]) => (
                        <option key={code} value={code}>{name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex flex-col gap-3">
                    <button
                      onClick={handleSaveAndContinue}
                      disabled={!selectedTranslation}
                      className="w-full bg-amber-900 text-white px-4 py-2 rounded-md hover:bg-amber-800 disabled:opacity-50"
                    >
                      {t('redirect_page.saveChoice')}
                    </button>
                    <button
                      onClick={handleContinueWithoutSaving}
                      className="w-full bg-amber-100 text-amber-900 px-4 py-2 rounded-md hover:bg-amber-200 disabled:opacity-50"
                    >
                      {t('redirect_page.continue')}
                    </button>
                  </div>
                </>
              )}
              
              {/* If parameter-driven but still showing UI (missing book/chapter), show loading message */}
              {isParameterDriven && (
                <div className="p-4 text-center">
                  <p className="text-amber-900">{t('redirect_page.redirecting')}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RedirectPage;